/**
 * composables/touch.js
 *
 * Does this device have a touchscreen?
 *
 * `any-pointer: coarse` and deliberately **not** `pointer: coarse`, which asks
 * about the *primary* pointing device. Windows declares a mouse as primary on
 * nearly every machine, keyboard attached or not, so a Windows tablet answered
 * "no" to that one and got the mouse layout. `any-pointer` asks the question we
 * actually mean: is there a coarse pointer available at all.
 *
 * Also deliberately NOT `useIsPhone` (composables/display.js), which is a width
 * gate and says in its own header that tablets must never change behaviour on
 * it. A 1280 px tablet and a 1280 px laptop are the same number.
 *
 * This decides **sizes only**. It used to decide which controls exist, which
 * made a wrong answer a broken screen rather than a cramped one -- see
 * `hasNativeCapture` in utils/camera.js for the capability question, which is
 * about the platform and cannot be asked of a media query.
 */
import { ref, onBeforeUnmount } from 'vue'

export function useIsTouch () {
  const query = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(any-pointer: coarse)')
    : null

  const isTouch = ref(query ? query.matches : false)

  if (query) {
    const onChange = (e) => { isTouch.value = e.matches }
    // `addEventListener` on a MediaQueryList is the modern spelling; Safari
    // before 14 only has `addListener`.
    if (query.addEventListener) query.addEventListener('change', onChange)
    else query.addListener(onChange)

    onBeforeUnmount(() => {
      if (query.removeEventListener) query.removeEventListener('change', onChange)
      else query.removeListener(onChange)
    })
  }

  return { isTouch }
}
